/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// core
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// interface
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CustomerAccountBillingDetails } from '@support/api/proxies/responses/api-2006.interface';
import { CustomerUsageSummaryResultsLines } from '@support/api/proxies/responses/api-2009.interface';
import { LineDetails } from '@support/api/proxies/responses/api-2007.interface';
// constants
import { ACCOUNT_LANDING_CONSTANTS } from '@experience/landing/common/constants/account-landing.constants';
import { SYMBOL_CONSTANTS } from '@support/shared/constants/device.constants';
import { CustomerNotifications, Alert } from '@support/api/proxies/responses/api-2025.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UserService } from '@support/shared/services/user.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { LineLandingService } from '@experience/landing/common/services/line-landing.service';
import { UsageBucketModel } from '@experience/landing/common/models/usage-bucket.model';
import { CustomerUsageSummaryResultsLineUsage } from '@support/api/proxies/responses/api-2009.interface';
/**
 * Component to host the Usage-Detail in Account-Landing
 */
@Component({
  selector: 'tmo-usage-detail',
  templateUrl: './usage-detail.component.html',
  styleUrls: ['../../../../../../style-sheets/components/html-progressbar.component.scss'],
})
export class UsageDetailComponent implements OnInit, OnDestroy {
  /**
   * Stores value of usage details
   */
  public usageDetails: Object[];
  /**
   * Boolean value to show more usage details
   */
  public showMoreDetails: boolean;
  /**
   * Value to show unlimited Data
   */
  public unlimitedData: string;
  /**
   * Store symbol constant value
   */
  public symbolConstants: Object;
  /**
   * Stores value of Notification
   */
  public notifications: Alert[];

  /**
   * @type {ConfigData}
   * @memberof UsageDetailComponent
   */
  @Input() public contentData: ConfigData;
  /**
   * @type {CustomerUsageSummaryResultsLines}
   * @memberof UsageDetailComponent
   */
  @Input() public lineUsageSummary: CustomerUsageSummaryResultsLines;
  /**
   * @type {CustomerAccountBillingDetails}
   * @memberof UsageDetailComponent
   */
  @Input() public customerAccountBillingDetails: CustomerAccountBillingDetails;
  /**
   * Stores lines object from API_2007
   */
  @Input() public lineDetails?: LineDetails;
  /**
   * Checks if plan is pay go type.
   */
  @Input() public isPayGoType?: boolean;
  /**
   * Checks fraudLockIndicator value
   */
  @Input() public fraudLockIndicator?: boolean;
  /**
   * display the notification
   */
  @Input() public fraudLockedNotification?: string;
  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * stores usage model
   */
  public usageBucketModel: UsageBucketModel;
  /**
   * to show fraud Indicator
   */
  public showIndicator: boolean;

  /**
   * Creates an instance of UsageDetailComponent.
   * @param {StateStoreUtilsService} stateStoreUtilsService
   * @param {ObjectService} objectService
   * @param {UserService} userService
   * @memberof UsageDetailComponent
   */
  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private userService: UserService,
    private objectService: ObjectService,
    private lineLandingService: LineLandingService
  ) {
    this.showMoreDetails = false;
    this.unlimitedData = ACCOUNT_LANDING_CONSTANTS.UNLIMITED;
    this.symbolConstants = SYMBOL_CONSTANTS;
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Initializes the data for usage detail component
   * Sets billingType and remainingDays
   * @memberof UsageDetailComponent
   */
  public ngOnInit(): void {
    this.usageBucketModel = new UsageBucketModel(
      this.lineUsageSummary,
      this.isPayGoType,
      this.objectService
    );
    this.getCustomerAccountAutopayStatus();
    this.showIndicator = this.fraudLockIndicator;
  }

  /**
   * To show hide remaining usage details
   * @returns void
   */
  public showUsageDetails(): void {
    this.showMoreDetails = !this.showMoreDetails;
  }
  /**
   * Unsubscribes for all subscriptions
   * @memberof UsageDetailComponent
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * Method to fetch notifications
   * @returns void
   */
  private getCustomerAccountAutopayStatus(): void {
    if (
      this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus().toLocaleUpperCase() ===
      ACCOUNT_LANDING_CONSTANTS.PREPAID
    ) {
      this.userService
        .getCustomerAccountAutopayStatus()
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe(
          (customerNotifications: CustomerNotifications) => {
            if (customerNotifications.alert.length) {
              this.notifications = this.objectService.get(customerNotifications, 'alert');
            } else {
              this.notifications = [];
            }
          },
          () => {
            this.lineLandingService.setErrorMessage(
              this.objectService.get(this.contentData, 'notificationError')
            );
          }
        );
    }
  }
  /**
   * Method to remove the fraud indicator notification from screen
   * @returns void
   */
  private removeFraudIndicatorAlert(): void {
    this.showIndicator = false;
  }

  public isDataPassArray(): boolean {
    let isDataPassArray = false;
    let dataPass: CustomerUsageSummaryResultsLineUsage | CustomerUsageSummaryResultsLineUsage[];
    if (this.usageBucketModel.usageBucket && this.usageBucketModel.usageBucket.bucket) {
      dataPass = this.usageBucketModel.usageBucket.bucket.dataPass;
    }
    if (dataPass) {
      Array.isArray(dataPass) ? (isDataPassArray = true) : '';
    }
    return isDataPassArray;
  }
}
